import type { ChatMessage } from "./client";
import type { EnglishLevel } from "../types";

const TRUNCATE_LIMIT = 1500;
const TRUNCATE_CHARS = 1200;

export const DIAGNOSIS_JSON_SCHEMA = `{
  "summary": "string",
  "fullTranslation": "string",
  "suggestedTextLevel": "beginner|intermediate|advanced",
  "suggestedLearnerLevel": "beginner|intermediate|advanced",
  "learnerLevelReason": "string",
  "blocks": [{ "type": "vocab|phrase|sentence|logic|background", "excerpt": "string", "translation": "string", "note": "string" }],
  "steps": [{ "title": "string", "content": "string", "checkQuestion": "string" }]
}`;

const DIAGNOSIS_SYSTEM_PROMPT = `You are an English reading coach for Chinese learners. Diagnose why the text is hard to read and design a short guided learning path.

Reply with ONLY valid JSON matching this schema (no markdown, no extra text):
${DIAGNOSIS_JSON_SCHEMA}

Rules:
- summary: 1-3 sentences in Chinese explaining the main reading difficulty.
- fullTranslation: concise natural Chinese translation of the analyzed text (the selected excerpt if only a selection was provided, otherwise the full text or the portion shown). Write readable paragraph-style Chinese, not word-by-word glosses.
- suggestedTextLevel: difficulty of this specific text for a general learner.
- suggestedLearnerLevel: your estimate of the learner's appropriate tutoring level based on the text and how they engaged with it.
- learnerLevelReason: brief Chinese explanation (1 sentence) for suggestedLearnerLevel.
- blocks: 2-6 specific difficulty points.
  - excerpt: quote a short English phrase or sentence from the text.
  - translation: concise Chinese translation of the excerpt (required for vocab, phrase, sentence types; optional for logic/background).
  - note: Chinese explanation or context — separate from translation; do not repeat the translation here.
- For every block with an English excerpt, always provide a direct Chinese translation line (translation field), distinct from the explanatory note.
- steps: 3-6 progressive learning steps in Chinese; each step teaches one chunk of understanding; checkQuestion is a simple comprehension check in Chinese.`;

function truncateText(text: string): { text: string; truncated: boolean } {
  if (text.length <= TRUNCATE_LIMIT) {
    return { text, truncated: false };
  }
  return { text: text.slice(0, TRUNCATE_CHARS), truncated: true };
}

function buildUserContent(
  fullText: string,
  selection: string | null,
  level: EnglishLevel,
): string {
  const selectionTrimmed = selection?.trim() ?? "";

  if (selectionTrimmed) {
    const context =
      fullText.length > 400
        ? `${fullText.slice(0, 200)}…\n[…]\n${fullText.slice(-120)}`
        : fullText;
    return [
      `Learner level: ${level}`,
      "",
      "Analyze ONLY the selected excerpt below. Use the surrounding context for reference only.",
      "",
      `Selected excerpt:\n${selectionTrimmed}`,
      "",
      `Surrounding context:\n${context}`,
    ].join("\n");
  }

  const { text, truncated } = truncateText(fullText);
  const truncationNote = truncated
    ? `\n\nNote: The original text was longer than ${TRUNCATE_LIMIT} characters. Only the beginning (~${TRUNCATE_CHARS} chars) is shown; focus your diagnosis on this portion.`
    : "";

  return `Learner level: ${level}\n\nText:\n${text}${truncationNote}`;
}

export function buildDiagnosisPrompt(
  text: string,
  selection: string | null,
  level: EnglishLevel,
): ChatMessage[] {
  return [
    { role: "system", content: DIAGNOSIS_SYSTEM_PROMPT },
    { role: "user", content: buildUserContent(text, selection, level) },
  ];
}

const SELECTION_CONTEXT_LIMIT = 2000;
const SELECTION_CONTEXT_CHARS = 1600;

export function buildSelectionQuestionPrompt(
  selectedText: string,
  readingContext: string,
  level: EnglishLevel,
): ChatMessage[] {
  const excerpt = selectedText.trim();
  const contextTrimmed = readingContext.trim();
  let contextBlock = "";
  if (contextTrimmed && contextTrimmed !== excerpt) {
    const ctx =
      contextTrimmed.length <= SELECTION_CONTEXT_LIMIT
        ? contextTrimmed
        : `${contextTrimmed.slice(0, SELECTION_CONTEXT_CHARS)}…`;
    contextBlock = `\n\nFull reading (for context only):\n${ctx}`;
  }

  return [
    {
      role: "system",
      content: `You are an English reading coach for Chinese learners. The user highlighted a passage while reading and wants help understanding it in context.

Reply in friendly Chinese. Use light markdown:
- **释义**：concise Chinese translation of the selection
- **讲解**：grammar, vocabulary, idioms, tone, or logic
- **延伸** (optional): brief background or related usage

Keep the answer focused on the highlighted text. Do not output JSON or raw code blocks.`,
    },
    {
      role: "user",
      content: [
        `Learner level: ${level}`,
        "",
        `Selected excerpt:\n${excerpt}`,
        contextBlock,
        "",
        "Explain the selected text in the context of this reading.",
      ]
        .filter(Boolean)
        .join("\n"),
    },
  ];
}

export function buildStepSimplifyPrompt(
  stepTitle: string,
  stepContent: string,
  checkQuestion: string | null,
  level: EnglishLevel,
): ChatMessage[] {
  return [
    {
      role: "system",
      content:
        "You are an English reading coach for Chinese learners. Rewrite the learning step explanation to be simpler and more concrete. Reply in plain Chinese text only (no JSON). Keep it shorter than the original.",
    },
    {
      role: "user",
      content: [
        `Learner level: ${level}`,
        `Step title: ${stepTitle}`,
        `Current explanation:\n${stepContent}`,
        checkQuestion ? `Check question: ${checkQuestion}` : "",
        "",
        "Provide a simpler, more detailed explanation for this step.",
      ]
        .filter(Boolean)
        .join("\n"),
    },
  ];
}
